import { useQueryClient } from '@tanstack/react-query';
import { ChannelClientQueryKeys } from 'gcs-common/clients/api/entities/channels/channel-api';
import { PusherChannelEvent } from 'gcs-common/pusher/pusher-constants';
import type { EventConfig } from 'gcs-common/pusher/pusher-context';
import { usePusherEvents } from 'gcs-common/pusher/pusher-hooks';
import { getChannelListChannelIds } from 'gcs-common/slices/channels/channelsSelectors';
import { channelUpdated } from 'gcs-common/slices/channels/channelsSlice';
import fetchCurrentUserChannels from 'gcs-common/slices/channels/channelsThunks/fetchCurrentUserChannels';
import handleChannelJoined from 'gcs-common/slices/channels/channelsThunks/handleChannelJoined';
import handleChannelLeft from 'gcs-common/slices/channels/channelsThunks/handleChannelLeft';
import handleChannelRemoved from 'gcs-common/slices/channels/channelsThunks/handleChannelRemoved';
import { getIsChatConnected } from 'gcs-common/slices/chatConnection/chatConnectionSelectors';
import { getCurrentUserId } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { updateCurrentChatUser } from 'gcs-common/slices/currentUser/currentUserThunks/updateCurrentChatUser';
import { getChannelMembers, getMembers } from 'gcs-common/slices/members/membersSelectors';
import { memberUpdated } from 'gcs-common/slices/members/membersSlice';
import { handleMemberJoined, removeMember, typingStarted, updateMember } from 'gcs-common/slices/members/membersThunks';
import { messageDeleted } from 'gcs-common/slices/messages/messagesSlice';
import addMessages from 'gcs-common/slices/messages/messagesThunks/addMessages';
import updateMessage from 'gcs-common/slices/messages/messagesThunks/updateMessage';
import { userUpdated } from 'gcs-common/slices/users/usersSlice';
import type { components } from 'gcs-common/types/openapi';
import { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export function usePusherChatSubscriptions() {
  const channelIds = useSelector(getChannelListChannelIds) as string[];
  const channelMembers = useSelector(getChannelMembers) as Record<string, string[]>;
  const members = useSelector(getMembers) as Record<string, { userId: string; channelId: string }>;
  const currentUserId = useSelector(getCurrentUserId);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isChatConnected = useSelector(getIsChatConnected);

  // Build userId -> channelIds[] map separately so we can keep a ref
  // that callbacks always read from, avoiding stale closures.
  const userChannelMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const channelId of channelIds) {
      if (!channelId) continue;
      for (const memberId of channelMembers[channelId] ?? []) {
        if (!memberId) continue;
        const userId = members[memberId]?.userId;
        if (!userId) continue;

        const existing = map.get(userId);
        if (existing) {
          if (!existing.includes(channelId)) {
            existing.push(channelId);
          }
        } else {
          map.set(userId, [channelId]);
        }
      }
    }
    return map;
  }, [channelIds, channelMembers, members]);

  // Ref keeps the latest map so Pusher callbacks (which are bound once and
  // never re-bound by usePusherEvents/addEventConfig) always read current data.
  const userChannelMapRef = useRef(userChannelMap);
  userChannelMapRef.current = userChannelMap;

  const eventConfigs = useMemo(() => {
    if (!isChatConnected) return [];

    const configs: EventConfig[] = [];

    // personal channel for the current user to receive
    // events beyond existing channels
    configs.push({
      channelName: `presence-current-user-${currentUserId}`,
      eventName: PusherChannelEvent.CHANNEL_LIST_UPDATED,
      callback: () => {
        dispatch(fetchCurrentUserChannels());
      },
    });

    // biome-ignore lint/complexity/noForEach: <explanation>
    channelIds.forEach((channelId) => {
      if (!channelId) return;

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MESSAGE_ADDED,
        callback: (message: components['schemas']['ChatMessageCreatedDTO']) => {
          dispatch(
            addMessages({
              channelMessages: { [message.channelId]: [message] },
            }),
          );

          // Reset typing status immediately when a new message is received
          const memberId = Object.keys(members).find(
            (key) => members[key]?.userId === message.authorId && members[key]?.channelId === message.channelId,
          );

          if (memberId) {
            dispatch(updateMember({ member: { id: memberId, isTyping: false } }));
          }

          if (!message.autogenerated) {
            dispatch(channelUpdated({ channel: { id: message.channelId, lastMessageDate: message.createdAt } }));
          }
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MESSAGE_UPDATED,
        callback: (message: components['schemas']['ChatMessageCreatedDTO']) => {
          dispatch(updateMessage({ message }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MESSAGE_DELETED,
        callback: (message: components['schemas']['ChatMessageCreatedDTO']) => {
          dispatch(messageDeleted({ messageId: message.id }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.CHANNEL_UPDATED,
        callback: (channel) => {
          dispatch(channelUpdated({ channel }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.CHANNEL_DELETED,
        callback: () => {
          dispatch(handleChannelRemoved({ channelId }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MEMBER_ADDED,
        callback: (member: components['schemas']['ChatMemberCreatedDTO']) => {
          if (member.userId === currentUserId) {
            dispatch(handleChannelJoined({ channelId }));
          }
          dispatch(handleMemberJoined({ member }));
          queryClient.invalidateQueries({
            queryKey: [ChannelClientQueryKeys.INFO_PANEL_DETAILS, channelId],
          });
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MEMBER_REMOVED,
        callback: (member: components['schemas']['ChatMemberCreatedDTO']) => {
          if (member.userId === currentUserId) {
            dispatch(handleChannelLeft({ channelId }));
          }
          dispatch(removeMember({ member }));
          queryClient.invalidateQueries({
            queryKey: [ChannelClientQueryKeys.INFO_PANEL_DETAILS, channelId],
          });
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.TYPING_STARTED,
        callback: (data: components['schemas']['PusherTypingEventResponseDTO']) => {
          dispatch(typingStarted({ channelId: data.channelId, userId: data.userId }));
        },
      });

      configs.push({
        channelName: `private-chat-channel-${channelId}`,
        eventName: PusherChannelEvent.MEMBER_UPDATED,
        callback: (member: components['schemas']['ChatMemberCreatedDTO']) => {
          dispatch(memberUpdated({ member }));
        },
      });
    });

    // USER_UPDATED subscriptions: one per unique user across all channels.
    // The callback reads from userChannelMapRef (not the closure) so it always
    // invalidates the correct set of channels even if the map changed after
    // the Pusher event was initially bound.
    userChannelMap.forEach((_userChannelIds, userId) => {
      configs.push({
        channelName: `private-user-${userId}`,
        eventName: PusherChannelEvent.USER_UPDATED,
        callback: (user) => {
          if (user.id === currentUserId) {
            dispatch(updateCurrentChatUser({ user }));
          }
          dispatch(userUpdated({ user }));
          for (const chId of userChannelMapRef.current.get(userId) ?? []) {
            queryClient.invalidateQueries({
              queryKey: [ChannelClientQueryKeys.INFO_PANEL_DETAILS, chId],
            });
          }
        },
      });
    });

    return configs;
  }, [userChannelMap, channelIds, members, dispatch, queryClient, currentUserId, isChatConnected]);

  usePusherEvents(eventConfigs);
}
